<template>
  <CustomModal
    :visible="visible"
    @update:visible="handleVisibilityUpdate"
    :width="'600px'"
    :visible_sections="{
      header: false,
      body: true,
      footer: true,
    }"
    :section_classes="{
      container: 'add_task_modal_container',
      body: 'add_task_modal_body',
      footer: 'add_task_modal_footer',
    }"
    :cross_variant="customModalCrossIconVariants.DEFAULT"
    source="add_task_modal"
  >
    <div class="flex flex-col gap-4">
      <SkeletonLoader v-if="showFullSkeleton" :page="loaderPages.ADD_TASK_MODAL" />

      <template v-else>
        <ProjectSelector
          ref="projectSelectorRef"
          :projects="availableProjects"
          v-model="selectedProject"
          :disabled="isEditMode"
        />

        <div v-if="messageContext" class="flex flex-row gap-3 rounded-lg bg_8 px-3 py-2.5">
          <div class="w-0.5 shrink-0 rounded-full bg-gray-400" />
          <div class="flex flex-col gap-1.5 min-w-0">
            <span class="text-[11px] font-semibold secondary_color uppercase tracking-wide">Associated Message</span>
            <div class="flex items-center gap-2">
              <ProfileIcon
                v-if="messageContext.sender"
                :data="messageContext.sender"
                :size="20"
                :font="8"
              />
              <span class="text-[13px] font-semibold primary_color">{{ messageContext.sender ? renderEntity(messageContext.sender) : 'Unknown' }}</span>
              <span v-if="messageContext.sentAt" class="text-[11px] secondary_color">{{ formatTimestamp(messageContext.sentAt, 'timestamp', formatTypes.centered) }}</span>
            </div>
            <span class="text-[13px] primary_color truncate">{{ messageContext.text || 'No message text' }}</span>
          </div>
        </div>

        <TaskForm
          v-if="showTaskForm"
          ref="taskFormRef"
          :users="fetchedUsers"
          :schema-attributes="schemaAttributes"
          :initial-data="taskData"
          :available-custom-attributes="availableCustomAttributes"
          :has-custom-properties="hasCustomProperties"
          :removable-field-slugs="pinnedCustomSlugs"
          @add-custom-property="handleAddCustomProperty"
          @remove-custom-property="handleRemoveCustomProperty"
        />

        <EmptyScreen
          v-else-if="selectedProject"
          source="tasks_no_schema"
          text="No task schema available"
          description="This project doesn't have a task schema configured."
          padding="py-8"
          css="size-16"
        />
      </template>
    </div>

    <template #footer>
      <div class="flex flex-row justify-end gap-2">
        <a-button
          class="secondary_medium"
          @click="handleVisibilityUpdate(false)"
        >
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          @click="handleCreateTask"
          :loading="loading || isWaitingMqtt"
          :disabled="
            selectedProject && (schemaLoading || schemaAttributes.length === 0)
          "
        >
          {{
            loading || isWaitingMqtt
              ? isEditMode
                ? "Updating Task..."
                : "Creating Task..."
              : isEditMode
                ? "Update Task"
                : "Create Task"
          }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, watch, computed, inject, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ProjectSelector from "@/src/components/task_components/ProjectSelector.vue";
import TaskForm from "@/src/components/task_components/TaskForm.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ProjectService from "@/src/api/ProjectService.js";
import ChatService from "@/src/api/ChatService.js";
import { renderEntity } from "@/src/utils/utils.js";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";
import { formatTypes } from "@/src/constants/dateTimeConstants.js";
import { custom_modal_cross_icon_variants as customModalCrossIconVariants } from "@/src/constants/customComponentConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  toFormOptions,
  transformFormDataForAPI,
} from "@/src/utils/dynamicFormUtils.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  projects: {
    type: Array,
    default: () => [],
  },
  users: {
    type: Array,
    default: () => [],
  },
  initialProject: {
    type: Object,
    default: null,
  },
  taskId: {
    type: String,
    default: null,
  },
  mode: {
    type: String,
    default: "create",
  },
  taskSchema: {
    type: Object,
    default: null,
  },
  initialData: {
    type: Object,
    default: () => ({}),
  },
  isWaitingMqtt: {
    type: Boolean,
    default: false,
  },
  messageContext: {
    type: Object,
    default: null,
  },
  messageId: {
    type: [String, Number],
    default: null,
  },
  groupId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["update:visible", "task-created"]);

const store = useStore();
const selectedSpace = inject("selectedSpace", ref(null));

const projectSelectorRef = ref(null);
const taskFormRef = ref(null);
const loading = ref(false);
const fetchedProjects = ref([]);
const schemaLoading = ref(false);
const isInitialLoad = ref(true);
const selectedProject = ref(props.initialProject);
const taskSchema = ref(null);
const fetchedUsers = ref([]);
const taskData = ref(null);
const originalTaskData = ref(null);
const currentSchemaProjectId = ref(null);

const isEditMode = computed(() => props.mode === "edit" && props.taskId);

const pinnedCustomSlugs = ref([]);

const getStorageKey = (projectId) => `task_custom_props_${projectId}`;

const loadPinnedSlugs = (projectId) => {
  if (!projectId) return [];
  try {
    const saved = localStorage.getItem(getStorageKey(projectId));
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
};

const savePinnedSlugs = (projectId, slugs) => {
  if (!projectId) return;
  localStorage.setItem(getStorageKey(projectId), JSON.stringify(slugs));
};

const currentUserId = computed(() => {
  const userData = store.getters["auth/userData"];
  return userData?.id || null;
});

const availableProjects = computed(() => {
  if (selectedSpace.value?.projects?.length > 0) {
    return selectedSpace.value.projects;
  }
  if (props.projects?.length > 0) {
    return props.projects;
  }
  return fetchedProjects.value;
});

const fetchProjectsIfNeeded = async () => {
  if (availableProjects.value.length > 0) return;
  try {
    const response = await ProjectService.tasks.fetchProjects({ page_size: 100 });
    const projects = response?.data?.projects || response?.projects || [];
    if (projects.length > 0) {
      fetchedProjects.value = projects;
    }
  } catch (error) {
    console.error("Failed to fetch projects:", error);
  }
};

const showFullSkeleton = computed(() => {
  return schemaLoading.value && isInitialLoad.value;
});

const showTaskForm = computed(() => {
  if (schemaLoading.value && !isInitialLoad.value) {
    return true;
  }
  return schemaAttributes.value.length > 0;
});

const transformAttr = (attr) => {
  const transformed = {
    id: attr.slug,
    slug: attr.slug,
    name: attr.name,
    type: attr.type,
    datatype: attr.type,
    description: attr.description,
    required: attr.is_required,
    is_required: attr.is_required,
    is_system: attr.is_system,
    is_unique: attr.is_unique,
    is_multi_value: attr.is_multi_value || false,
    ordering: attr.ordering,
    default_value: attr.default_value,
  };
  if (attr.options && Array.isArray(attr.options)) {
    transformed.options = toFormOptions(attr.options);
  }
  if (attr.type === "user") {
    transformed.options = fetchedUsers.value;
  }
  return transformed;
};

const SUPPORTED_CUSTOM_TYPES = ["select", "multi_select", "user", "date", "datetime"];

const schemaAttributes = computed(() => {
  if (!taskSchema.value?.attributes) return [];
  const allAttrs = taskSchema.value.attributes;
  const defaultAttrs = allAttrs.filter((a) => a.is_default === true);
  const pinnedCustomAttrs = allAttrs.filter(
    (a) => a.is_default !== true && pinnedCustomSlugs.value.includes(a.slug),
  );
  return [...defaultAttrs, ...pinnedCustomAttrs].map(transformAttr);
});

const availableCustomAttributes = computed(() => {
  if (!taskSchema.value?.attributes) return [];
  return taskSchema.value.attributes
    .filter(
      (a) =>
        a.is_default !== true &&
        !pinnedCustomSlugs.value.includes(a.slug) &&
        SUPPORTED_CUSTOM_TYPES.includes(a.type),
    )
    .map(transformAttr);
});

const hasCustomProperties = computed(() =>
  (taskSchema.value?.attributes || []).some(
    (a) => a.is_default !== true && SUPPORTED_CUSTOM_TYPES.includes(a.type),
  ),
);

const handleAddCustomProperty = (slug) => {
  if (!pinnedCustomSlugs.value.includes(slug)) {
    pinnedCustomSlugs.value = [...pinnedCustomSlugs.value, slug];
    savePinnedSlugs(selectedProject.value?.id, pinnedCustomSlugs.value);
  }
};

const handleRemoveCustomProperty = (slug) => {
  pinnedCustomSlugs.value = pinnedCustomSlugs.value.filter((s) => s !== slug);
  savePinnedSlugs(selectedProject.value?.id, pinnedCustomSlugs.value);
};

const buildCreatePayload = (formData) => {
  const customSlugs = new Set(pinnedCustomSlugs.value);
  const standard = {};
  const attributes = {};

  Object.entries(formData).forEach(([key, value]) => {
    if (customSlugs.has(key)) {
      if (value !== null && value !== undefined) {
        attributes[key] = value;
      }
    } else {
      standard[key] = value;
    }
  });

  return Object.keys(attributes).length > 0
    ? { ...standard, attributes }
    : standard;
};

const fetchTaskSchema = async (projectId) => {
  if (projectId === null || projectId === undefined) {
    schemaLoading.value = false;
    return;
  }

  if (currentSchemaProjectId.value === projectId && taskSchema.value) {
    schemaLoading.value = false;
    return;
  }

  try {
    schemaLoading.value = true;
    currentSchemaProjectId.value = projectId;
    const response = await ProjectService.tasks.fetchTaskSchema(projectId);

    if (response?.success && response?.schema) {
      taskSchema.value = response.schema;
    } else {
      message.error("Failed to load task schema");
      taskSchema.value = null;
      currentSchemaProjectId.value = null;
    }
  } catch (error) {
    console.error("Error fetching task schema:", error);
    message.error("Failed to load task schema");
    taskSchema.value = null;
    currentSchemaProjectId.value = null;
  } finally {
    schemaLoading.value = false;
  }
};

const fetchUsers = () => {
  try {
    const storeUsers = store.getters["users/users"] || [];

    fetchedUsers.value = storeUsers.map((user) => ({
      id: user.id,
      value: user.id,
      label: user.name || user.email,
      name: user.name,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      profile_pic: user.profile_pic,
      logo_url: user.logo_url,
      avatar_color: user.avatar_color,
      data: user,
    }));
  } catch (error) {
    console.error("Error fetching users:", error);
    fetchedUsers.value = [];
  }
};

const fetchTaskDetails = async () => {
  if (!props.taskId || !selectedProject.value?.id) {
    return;
  }

  try {
    const response = await ProjectService.tasks.getTask(
      selectedProject.value.id,
      props.taskId,
    );

    if (response?.success && response?.task) {
      const formData = {};

      if (response.task.attributes) {
        Object.keys(response.task.attributes).forEach((key) => {
          const value =
            response.task.attributes[key]?.value ??
            response.task.attributes[key];
          formData[key] = value;
        });
      }

      originalTaskData.value = { ...formData };
      taskData.value = formData;
    } else {
      message.error("Failed to load task details");
    }
  } catch (error) {
    console.error("Error fetching task details:", error);
    message.error("Failed to load task details");
  }
};

const resetForm = () => {
  taskFormRef.value?.reset();
  taskData.value = null;
  originalTaskData.value = null;
};

const initializeFormWithInitialData = () => {
  const initialData = { ...props.initialData };

  // Set current user as default owner for new tasks
  if (!isEditMode.value && currentUserId.value && !initialData.owner) {
    initialData.owner = currentUserId.value;
  }

  if (Object.keys(initialData).length > 0) {
    taskData.value = initialData;
  }
};

const focusFirstField = async () => {
  await nextTick();
  taskFormRef.value?.focusFirstField();
};

const handleVisibilityUpdate = (newVal) => {
  emit("update:visible", newVal);
};

const handleCreateTask = async () => {
  try {
    if (!selectedProject.value) {
      message.warning("Please select a project");
      projectSelectorRef.value?.openDropdown();
      return;
    }

    const formData = taskFormRef.value?.getData();

    if (!formData.title || formData.title.trim() === "") {
      message.error("Please enter task name");
      return;
    }

    await taskFormRef.value?.validate();

    loading.value = true;

    let response;
    if (isEditMode.value) {
      const result = transformFormDataForAPI(
        formData,
        schemaAttributes.value,
        originalTaskData.value || {},
        { allowExplicitFalse: true },
      );

      const changedAttributes = result.attributes || {};

      if (Object.keys(changedAttributes).length === 0) {
        emit("update:visible", false);
        return;
      }

      response = await ProjectService.tasks.updateTask(
        selectedProject.value.id,
        props.taskId,
        { task: { attributes: changedAttributes } },
      );

      if (response?.success) {
        const taskData = response.data?.task || response.data || formData;

        emit("task-created", {
          project: selectedProject.value,
          task: taskData,
          isUpdate: true,
        });
      } else {
        message.error(response?.error || "Failed to update task");
      }
    } else if (props.messageId) {
      // Create task via chat service — links the task to the message
      const groupId = props.groupId || store.getters["chats/activeChat"]?.id || null;
      response = await ChatService.linkMessageToTask(props.messageId, {
        project_id: selectedProject.value.id,
        group_id: groupId ? Number(groupId) : null,
        task: buildCreatePayload(formData),
      });

      if ((response?.status === 200 || response?.status === 201) && response?.success) {
        // Full success — task created + thread reply sent
        // The reply message will arrive via MQTT for both main panel and embedded chats
        const taskData = response.data?.task || response.task || response.data || formData;
        message.success("Task created and linked to message");

        emit("task-created", {
          project: selectedProject.value,
          task: taskData,
          responseMessage: response.message || null,
        });
      } else if (response?.status === 207 && response?.partial) {
        // Partial success — task created but thread reply failed. Do NOT retry.
        const taskData = response.data?.task || response.task || formData;
        message.warning("Task created but the message couldn't be posted to the thread");

        emit("task-created", {
          project: selectedProject.value,
          task: taskData,
        });
      } else {
        message.error(response?.error || "Failed to create task");
      }
    } else {
      response = await ProjectService.tasks.createTask(
        selectedProject.value.id,
        buildCreatePayload(formData),
      );

      if (response?.success) {
        const taskData = response.data?.task || response.data || formData;
        emit("task-created", {
          project: selectedProject.value,
          task: taskData,
        });
      } else {
        message.error(response?.error || "Failed to create task");
      }
    }
  } catch (error) {
    console.error("Error saving task:", error);

    if (!error.errorFields) {
      message.error(
        `An error occurred while ${isEditMode.value ? "updating" : "creating"} the task`,
      );
    }
  } finally {
    loading.value = false;
  }
};

watch(
  () => selectedProject.value,
  async (newProject, oldProject) => {
    if (newProject?.id && newProject?.id !== oldProject?.id && props.visible) {
      pinnedCustomSlugs.value = loadPinnedSlugs(newProject.id);
      await fetchTaskSchema(newProject.id);
      // Initialize form with default owner for new tasks
      if (!isEditMode.value) {
        initializeFormWithInitialData();
      }
      await focusFirstField();
    } else if (!newProject?.id) {
      taskSchema.value = null;
      currentSchemaProjectId.value = null;
      schemaLoading.value = false;
    }
  },
);

watch(
  () => props.visible,
  async (newVal) => {
    if (!newVal) {
      resetForm();
      taskSchema.value = null;
      currentSchemaProjectId.value = null;
      schemaLoading.value = false;
      isInitialLoad.value = true;
    } else {
      isInitialLoad.value = true;
      schemaLoading.value = true;

      selectedProject.value = props.initialProject;
      pinnedCustomSlugs.value = loadPinnedSlugs(
        props.initialProject?.id || selectedProject.value?.id,
      );

      await Promise.all([fetchUsers(), fetchProjectsIfNeeded()]);

      if (isEditMode.value) {
        if (props.initialProject?.id) {
          await fetchTaskSchema(props.initialProject.id);
          await fetchTaskDetails();
        } else {
          schemaLoading.value = false;
        }
      } else {
        if (props.initialProject?.id) {
          await fetchTaskSchema(props.initialProject.id);
        } else {
          await fetchTaskSchema(0);
        }
        initializeFormWithInitialData();
      }

      isInitialLoad.value = false;
      await focusFirstField();
    }
  },
);

watch(
  () => [props.mode, props.taskId],
  async ([newMode, newTaskId]) => {
    if (
      props.visible &&
      newMode === "edit" &&
      newTaskId &&
      selectedProject.value?.id
    ) {
      await fetchTaskSchema(selectedProject.value.id);
      await fetchTaskDetails();
    }
  },
);

watch(
  () => props.initialProject,
  (newVal) => {
    selectedProject.value = newVal;
  },
);


onMounted(() => {
  fetchUsers();
});
</script>

<style scoped>
.add_task_modal_container {
  overflow: visible;
}

.add_task_modal_body {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.add_task_modal_footer {
  padding: 16px 24px;
  border-top: 1px solid var(--white-opacity-8);
}
</style>
